import { useForm } from 'react-hook-form'

import { useMyMonitoringCreateSiteZone } from '@apps-common/apis/my-monitoring/zones/create-site-zone'
import { useCreateUnitCustomZone } from '@apps-common/apis/portal/zones'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { ModalBody, ModalFooter } from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { IUnitCustomZoneModel } from '@apps-common/types/portal/unit'

interface IProps {
  unitId: string
  closeCb: (role: string) => void
}

export const ZoneAddForm: React.FC<IProps> = ({ unitId, closeCb }) => {
  const useCreateZoneHook = useProjectHook(useCreateUnitCustomZone, useMyMonitoringCreateSiteZone)

  const { mutate: create, isPending: createIsPending } = useCreateZoneHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<IUnitCustomZoneModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      unitCustomZoneDescriptionId: 0,
      unitId,
      zone: '',
      description: '',
      mapPartition: false,
      partition: '',
      editedBy: '',
      editDate: new Date().toISOString()
    }
  })

  const submit = (values: IUnitCustomZoneModel) => {
    if (values?.partition && values?.partition?.length > 0) {
      values.mapPartition = true
    }

    create({ model: values })
  }

  const isLoading = isSubmitting || createIsPending

  return (
    <>
      <ModalBody classes='!pt-[90px]'>
        <form noValidate id='unit-zone-create-form' onSubmit={handleSubmit(submit)}>
          <VertSpacerMd />
          <InputSM
            key='description'
            type='text'
            placeholder='Enter description'
            label='Zone description'
            id='description'
            showLabel
            disabled={isLoading}
            registration={register('description', {
              required: FORM_DEFAULT_REQUIRED,
              maxLength: {
                value: 30,
                message: 'No longer than 30 characters'
              }
            })}
            errors={errors.description}
          />
          <InputSM
            key='zone'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='zone'
            showLabel
            disabled={isLoading}
            registration={register('zone', {
              required: FORM_DEFAULT_REQUIRED,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.zone}
          />
          <InputSM
            key='partition'
            type='text'
            placeholder='0 to 99'
            label='Partition (If required)'
            id='userPartition'
            showLabel
            disabled={isLoading}
            registration={register('partition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.partition}
          />
          <VertSpacerLg />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm type='submit' form='unit-zone-create-form' loading={isLoading} text='Create' className='action-success' />
          <ButtonRoundedSm
            type='button'
            loading={isLoading}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
